import type { ActionOption, PlayerCommand } from "@coup/shared";
import { useCallback, useMemo, useState } from "react";
import { ICONS } from "../../art";
import { Icon } from "../../components/Icon";
import { Spectators } from "../../components/Spectators";
import { Stage, fromYou, seatPositions } from "../../components/Stage";
import stage from "../../components/Stage.module.css";
import { useStore } from "../../state/store";
import { ActionMenu } from "./ActionMenu";
import { AnimLayer } from "./AnimLayer";
import { Deck, Exiled, Treasury } from "./Centre";
import { CourtRecord } from "./CourtRecord";
import { ExchangeModal, LoseCardModal, WinModal } from "./Modals";
import { PromptDock } from "./PromptDock";
import { ReferencePanel } from "./ReferencePanel";
import { Seat } from "./Seat";
import { caption, makeNames } from "./names";
import { useDisplayedGame } from "./useDisplayedGame";
import { usePanels } from "./usePanels";
import styles from "./Table.module.css";

export function Table({ onBackToLobby }: { onBackToLobby: () => void }) {
  const lobby = useStore((s) => s.lobby)!;
  const send = useStore((s) => s.send);
  const { game, anims, onTick } = useDisplayedGame();
  const { left, right, toggle } = usePanels();
  const [menu, setMenu] = useState<string>();

  const view = game.view;
  const names = useMemo(() => makeNames(lobby.seats, view.you), [lobby.seats, view.you]);
  const isHost = lobby.hostId === view.you;
  const command = useCallback((cmd: PlayerCommand) => send({ t: "cmd", command: cmd }), [send]);
  const closeMenu = useCallback(() => setMenu(undefined), []);

  const players = fromYou(view.players, view.you);
  const positions = seatPositions(players.length, 340, 320);
  const seatPos = (id: string) =>
    positions[players.findIndex((p) => p.id === id)] ?? { x: 430, y: 450 };
  const canAct = view.options.t === "declare";
  const openMenu = canAct
    ? (m: string) => setMenu((cur) => (cur === m ? undefined : m))
    : undefined;
  const menuOpen = canAct ? menu : undefined;

  let menuX = 0;
  let menuY = 0;
  if (menuOpen === "treasury") [menuX, menuY] = [180, 400];
  else if (menuOpen === "deck") [menuX, menuY] = [430, 400];
  else if (menuOpen) {
    const pos = positions[players.findIndex((p) => p.id === menuOpen)]!;
    menuX = pos.x < 430 ? pos.x + 90 : pos.x - 90 - 260;
    menuY = Math.min(Math.max(10, pos.y - 110), 600);
  }

  const pick = (o: ActionOption) => {
    command({ t: "declare", kind: o.kind, ...(o.target && { target: o.target }) });
    setMenu(undefined);
  };

  const o = view.options;
  const modal =
    o.t === "loseCard" ? (
      <LoseCardModal view={view} names={names} onCommand={command} />
    ) : o.t === "exchange" ? (
      <ExchangeModal view={view} names={names} onCommand={command} />
    ) : view.phase.t === "over" ? (
      <WinModal
        view={view}
        names={names}
        isHost={isHost}
        onPlayAgain={() => send({ t: "host", op: { op: "again" } })}
        onBack={onBackToLobby}
      />
    ) : null;

  return (
    <div
      className={styles.layout}
      style={{ gridTemplateColumns: `${left ? 260 : 48}px 1fr ${right ? 320 : 48}px` }}
    >
      <aside className={styles.left}>
        {left ? (
          <ReferencePanel
            code={lobby.code}
            responseMs={lobby.responseMs}
            isHost={isHost}
            onEndMatch={() => send({ t: "host", op: { op: "end" } })}
            onCollapse={() => toggle("left")}
          />
        ) : (
          <button
            type="button"
            className={styles.rail}
            aria-label="Expand reference panel"
            onClick={() => toggle("left")}
          >
            <Icon path={ICONS.collapseRight} size={18} />
          </button>
        )}
      </aside>

      <main className={styles.main} onClick={(e) => e.target === e.currentTarget && closeMenu()}>
        <Stage>
          <div className={stage.felt} onClick={closeMenu} />
          <Treasury view={view} names={names} menu={menuOpen} onOpen={openMenu} />
          <Deck view={view} names={names} menu={menuOpen} onOpen={openMenu} />
          <div className={styles.caption}>{caption(view, names)}</div>
          <Exiled view={view} names={names} />
          {players.map((p, i) => (
            <Seat
              key={p.id}
              player={p}
              view={view}
              names={names}
              x={positions[i]!.x}
              y={positions[i]!.y}
              responseMs={lobby.responseMs}
              selected={menuOpen === p.id}
              onPick={
                openMenu && p.id !== view.you && p.cards > 0 ? () => openMenu(p.id) : undefined
              }
            />
          ))}
          {menuOpen && (
            <ActionMenu
              view={view}
              names={names}
              menu={menuOpen}
              x={menuX}
              y={menuY}
              onPick={pick}
              onClose={closeMenu}
            />
          )}
          <PromptDock
            view={view}
            names={names}
            responseMs={lobby.responseMs}
            onCommand={command}
            onTick={onTick}
          />
          <AnimLayer anims={anims} names={names} seatPos={seatPos} />
          {modal && <div className={styles.scrim} />}
          {modal}
        </Stage>
        <Spectators />
      </main>

      <aside className={styles.right}>
        {right ? (
          <CourtRecord view={view} names={names} onCollapse={() => toggle("right")} />
        ) : (
          <button
            type="button"
            className={styles.rail}
            aria-label="Expand action log"
            onClick={() => toggle("right")}
          >
            <Icon path={ICONS.collapseLeft} size={18} />
          </button>
        )}
      </aside>
    </div>
  );
}
